// src/components/admin/tabs/pointsCenter/PointsOverviewPane.jsx
// 积分中心 · 总览：时段/粒度控制条 + 5 KPI + 积分vs花销双轨趋势 + 类型分布 + 告警用户。
import { AppSelect } from '../../../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { fetchPointAccounts, fetchPointStats } from "../../../../services/adminService";
import { displayUserId, formatMoney } from "../../adminUtils";
import {
  EmptyBlock,
  ErrorNotice,
  Panel,
  ProjectCombobox,
  Segmented,
  StatCard,
  StatusBadge,
  TypeBadge,
} from "../../adminUi.jsx";
import { DateRangeControl, DualAxisChart } from "./pointsShared.jsx";
import {
  ACCENT,
  formatPoints,
  GRANULARITY_OPTIONS,
  rangeOfPreset,
  recommendGranularity,
} from "./pointsShared.js";

const GRANULARITY_LABEL = { hour: "小时", day: "天", month: "月", year: "年" };

const warningMeta = (row) => {
  if (row?.warningType === "negative") {
    return { label: "余额为负", color: "var(--color-error)" };
  }
  if (row?.warningType === "excessive") {
    return { label: "今日超量", color: "var(--color-warning)" };
  }
  return { label: "告警", color: "var(--color-warning)" };
};

const PointsOverviewPane = ({ projectOptions = [] }) => {
  const [range, setRange] = useState(() => rangeOfPreset("7d"));
  const [projectFilter, setProjectFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [granularity, setGranularity] = useState("day");
  const [stats, setStats] = useState(null);
  const [warnings, setWarnings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState("");
  const requestSeq = useRef(0);

  // 用户筛选选项：账户列表（带积分的用户）
  useEffect(() => {
    let alive = true;
    fetchPointAccounts({ limit: 1000 })
      .then((res) => {
        if (!alive) return;
        const items = res?.items || res?.accounts || [];
        setUserOptions(items.map((item) => item.userId).filter(Boolean));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const applyRange = (next) => {
    setRange(next);
    setGranularity(recommendGranularity(next.from, next.to));
  };

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    try {
      const [statsRes, warningRes] = await Promise.all([
        fetchPointStats({
          from: range.from,
          to: range.to,
          granularity,
          dimension: "type",
          projectId: projectFilter || undefined,
          userId: userFilter || undefined,
        }),
        fetchPointAccounts({ onlyWarning: true, limit: 100 }),
      ]);
      if (seq !== requestSeq.current) return;
      setStats(statsRes || null);
      setWarnings(warningRes || null);
      setLoadedOnce(true);
    } catch (e) {
      console.error(e);
      if (seq === requestSeq.current) setError(e?.message || "积分统计加载失败（统计接口可能未上线）");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [range, granularity, projectFilter, userFilter]);

  useEffect(() => { load(); }, [load]);

  const summary = stats?.summary || {};
  const series = useMemo(() => (stats?.series || []).map((row) => ({
    bucket: row.bucket,
    points: Number(row.points || 0),
    costCny: Number(row.costCny || 0),
    count: Number(row.count || 0),
  })), [stats]);

  // 按生成类型分布（dimension=type 的 groups），按积分消耗排序
  const typeGroups = useMemo(() => {
    const rows = (stats?.groups || []).map((row) => ({
      key: row.key,
      points: Number(row.points || 0),
      costCny: Number(row.costCny || 0),
      count: Number(row.count || 0),
    })).filter((row) => row.points > 0 || row.count > 0);
    rows.sort((a, b) => b.points - a.points);
    return rows;
  }, [stats]);
  const typeTotalPoints = typeGroups.reduce((acc, row) => acc + row.points, 0);

  const warningRowsAll = warnings?.items || warnings?.accounts || [];
  const warningRows = userFilter
    ? warningRowsAll.filter((row) => row.userId === userFilter)
    : warningRowsAll;
  const warningCount = warnings?.summary?.warningCount ?? warnings?.warningCount ?? warningRows.length;

  return (
    <div className="space-y-4">
      {/* 控制条：时段 + 粒度 */}
      <div
        className="rounded-lg border px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-3"
        style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-secondary)" }}
      >
        <div className="flex items-center gap-2">
          <span className="text-label">时段</span>
          <DateRangeControl from={range.from} to={range.to} onRangeChange={applyRange} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-label">粒度</span>
          <Segmented options={GRANULARITY_OPTIONS} value={granularity} onChange={setGranularity} ariaLabel="统计粒度" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-label">项目</span>
          <ProjectCombobox
            value={projectFilter}
            onChange={setProjectFilter}
            projectOptions={projectOptions}
            placeholder="全部项目"
            maxWidth={220}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-label">用户</span>
          <AppSelect
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="h-8 rounded-md px-2 text-xs outline-none transition-colors"
            style={{
              backgroundColor: "var(--color-bg-overlay)",
              border: "1px solid var(--color-border-primary)",
              color: userFilter ? "var(--color-text-primary)" : "var(--color-text-muted)",
              maxWidth: 180,
            }}
          >
            <option value="">全部用户</option>
            {userOptions.map((uid) => (
              <option key={uid} value={uid}>{displayUserId(uid)}</option>
            ))}
          </AppSelect>
        </div>
        <button
          type="button"
          onClick={load}
          title="刷新"
          className="w-8 h-8 ml-auto rounded-md border flex items-center justify-center cursor-pointer transition-colors duration-150"
          style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && <ErrorNotice message={error} />}

      {/* KPI 数据带 */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <StatCard label="积分消耗" value={formatPoints(summary.pointsConsumed)} color={ACCENT} />
        <StatCard label="真实花销" value={formatMoney(summary.costCny)} color="var(--color-blue-primary)" />
        <StatCard label="生成次数" value={formatPoints(summary.generationCount)} />
        <StatCard label="活跃用户" value={summary.activeUsers ?? 0} />
        <StatCard label="告警中" value={warningCount || 0} color={warningCount ? "var(--color-error)" : undefined} />
      </div>

      {/* 双轨趋势 */}
      <Panel
        title="积分 vs 花销"
        sub={`按${GRANULARITY_LABEL[granularity] || "天"}统计 · 柱=积分 · 折线=¥ · 悬停查看明细`}
        right={<span className="font-mono-num text-2xs" style={{ color: "var(--color-text-muted)" }}>{series.length} 个统计点</span>}
      >
        {!loadedOnce && loading ? (
          <div className="space-y-2">
            <div className="skeleton" style={{ height: 232 }} />
            <div className="skeleton h-3 w-1/3" />
          </div>
        ) : (
          <DualAxisChart rows={series} granularity={granularity} />
        )}
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* 按生成类型分布 */}
        <Panel title="按生成类型分布" sub={`${range.from} ~ ${range.to} · 按积分消耗`}>
          {!typeGroups.length ? (
            <EmptyBlock text="该时段内没有生成记录" hint="调整时段后重试" />
          ) : (
            <div className="space-y-3">
              {typeGroups.map((row) => {
                const share = typeTotalPoints > 0 ? row.points / typeTotalPoints : 0;
                return (
                  <div key={row.key} className="flex items-center gap-3">
                    <span className="w-12 shrink-0"><TypeBadge type={row.key} /></span>
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.max(2, share * 100)}%`, backgroundColor: ACCENT, opacity: 0.8 }}
                      />
                    </div>
                    <span className="font-mono-num text-xs w-20 text-right shrink-0" style={{ color: "var(--color-text-primary)" }}>
                      {formatPoints(row.points)}
                    </span>
                    <span className="font-mono-num text-2xs w-12 text-right shrink-0" style={{ color: "var(--color-text-muted)" }}>
                      {(share * 100).toFixed(1)}%
                    </span>
                  </div>
                );
              })}
              <div className="pt-2 border-t text-2xs font-mono-num" style={{ borderColor: "var(--color-border-secondary)", color: "var(--color-text-muted)" }}>
                合计 {formatPoints(typeTotalPoints)} 分 · {formatMoney(typeGroups.reduce((a, r) => a + r.costCny, 0))}
              </div>
            </div>
          )}
        </Panel>

        {/* 告警用户 */}
        <Panel
          title="告警用户"
          sub="超每日消耗阈值 / 余额为负"
          right={<StatusBadge label={`${warningCount || 0} 个账户`} color={warningCount ? "var(--color-error)" : "var(--color-success)"} />}
        >
          {!warningRows.length ? (
            <EmptyBlock text="当前没有告警账户" hint="超阈值或负余额账户会出现在这里" />
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar -mr-1 pr-1">
              {warningRows.map((row) => {
                const meta = warningMeta(row);
                return (
                  <div
                    key={row.userId}
                    className="rounded-lg border px-3.5 py-2.5 flex items-center gap-3"
                    style={{
                      backgroundColor: "var(--color-bg-tertiary)",
                      borderColor: `color-mix(in srgb, ${meta.color} 35%, transparent)`,
                    }}
                  >
                    <AlertTriangle size={15} className="shrink-0" style={{ color: meta.color }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium truncate" style={{ color: "var(--color-text-primary)" }}>
                        {displayUserId(row.userId)}
                      </div>
                      <div className="text-2xs font-mono-num mt-0.5" style={{ color: "var(--color-text-muted)" }}>
                        今日 {formatPoints(row.todayConsumed)} / 阈值 {formatPoints(row.dailyWarningThreshold)} 分 · 余额 {formatPoints(row.balance)}
                      </div>
                    </div>
                    <StatusBadge label={meta.label} color={meta.color} />
                  </div>
                );
              })}
            </div>
          )}
          {warningRows.length > 0 && (
            <div className="pt-2 mt-1 border-t text-2xs" style={{ borderColor: "var(--color-border-secondary)", color: "var(--color-text-muted)" }}>
              限额策略上线后，超限告警（WARN）也会汇总到这里。
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default PointsOverviewPane;
